import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Badge,
  Button,
  Divider,
  Layout,
  Menu,
  Popover,
  Switch,
  Tag,
} from "antd";
import {
  ChevronLeftDoubleIcon,
  ChevronRightDoubleIcon,
  InboxIcon,
  OperatorUserIcon,
  SendMessageIcon,
  UsersIcon,
} from "assets/icons";
import { COLORS, STORAGE_KEYS, USER_TYPE } from "constants/appConstant";
import SwitchWorkspace from "pages/admin/switchWorkspace";
import { AuthInfo } from "stores/slices/auth";
import {
  doUpdateConversation,
  getConversationList,
} from "stores/slices/conversation";
import { changeLayout, setCollapsedMenu } from "stores/slices/layout";
import {
  doGetUserSettingEvent,
  doUpdateWorkspaceUser,
  getWorkspaceUserInfo,
  workspaceUserDetail,
} from "stores/slices/workspaceUser";
import { requestForToken } from "useFirebase/firebase";
import StorageHelper from "utils/storeHelper";

import logo from "../../assets/images/2d-logo-black-transparent-webp.webp";
import iconLogo from "../../assets/images/icon-black-transparent-webp.webp";
import {
  doGetWorkspaceEvent,
  doGetWorkspacePage,
  getRealtimeWorkspace,
  getWorkspaceInfo,
  WorkspaceInfo,
} from "../../stores/slices/workspace";

import styles from "../styles.module.scss";

const OperatorSidebar = props => {
  const {
    broken,
    setBroken,
    collapsed,
    setCollapsed,
    currentMenu,
    setCurrentMenu,
  } = props;

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const navigate = useNavigate();
  const authInfo = useSelector(AuthInfo);
  const workspaceUserInfo = useSelector(workspaceUserDetail);
  let conversationList = useSelector(getConversationList);
  const workspaceInfo = useSelector(WorkspaceInfo);
  const workspaceRealtime = useSelector(getRealtimeWorkspace);
  const [workspaceListItem, setWorkspaceListItem] = useState([]);
  const [numberAwaitingReplies, setNumberAwaitingReplies] = useState(0);
  const [isOpenSwitchWorkspace, setIsOpenSwitchWorkspace] = useState(false);

  const handleUnlockConversation = async key => {
    let request;
    if (conversationList && conversationList.length > 0 && key !== "inbox") {
      conversationList.forEach(async conversationItem => {
        //  Unlock conversation
        if (
          authInfo?.id === conversationItem?.lockUserId &&
          conversationItem?.lock
        ) {
          request = {
            id: conversationItem?.id,
            lockConversation: false,
          };
          await dispatch(doUpdateConversation(request));
        }
      });
    }
  };

  const doGetWorkspace = async () => {
    const params = {
      pageNumber: 1,
      pageSize: 10,
      sortFieldWorkspace: "createdTime",
      sortDirection: "DESC",
    };
    const { payload } = await dispatch(doGetWorkspacePage(params));
    if (payload && payload.content && payload.content.length > 0) {
      setWorkspaceListItem(payload.content);
    }
  };

  const handleChangeMenu = e => {
    handleUnlockConversation(e.key);
    switch (e.key) {
      case "inbox":
        setCurrentMenu(e.key);
        navigate("/inbox/messages");
        break;
      case "send-message":
        setCurrentMenu(e.key);
        navigate("/send-message");
        break;
      case "broadcast-list":
        setCurrentMenu(e.key);
        navigate("/broadcast-list");
        break;
      case "customer":
        setCurrentMenu(e.key);
        navigate("/customer/all-customer/all");
        break;
      default:
        setCurrentMenu("");
        break;
    }
  };

  const handleRequestAvailableNewMessage = async () => {
    if (
      workspaceInfo?.workspaceUser?.availableNewMessage !== undefined &&
      !workspaceInfo?.workspaceUser?.availableNewMessage
      // workspaceInfo?.workspaceUser?.availableNewMessage !== null &&
    ) {
      Notification.requestPermission().then(permission => {
        if (permission === "granted") {
          requestForToken().then(token => {
            if (token) {
              const request = {
                workspaceId: authInfo.workspaceId,
                userId: authInfo.id,
                availableNewMessage: true,
                fcmToken: token,
              };
              handleUpdateAvailableNewMessage(request);
            }
          });
        } else {
          const request = {
            workspaceId: authInfo.workspaceId,
            userId: authInfo.id,
            availableNewMessage: true,
            fcmToken: "",
          };
          handleUpdateAvailableNewMessage(request);
        }
      });
    } else {
      const request = {
        workspaceId: authInfo.workspaceId,
        userId: authInfo.id,
        availableNewMessage: false,
      };
      handleUpdateAvailableNewMessage(request);
    }
  };

  const handleUpdateAvailableNewMessage = async data => {
    const { payload } = await dispatch(doUpdateWorkspaceUser(data));
    if (payload && payload.status === 200) {
      await dispatch(
        getWorkspaceUserInfo({
          userId: authInfo.id,
          workspaceId: authInfo.workspaceId,
        })
      );
      await dispatch(getWorkspaceInfo(authInfo.workspaceId));
    }
  };
  const getItem = ({ label, key, icon, children, disabled }) => {
    return {
      key,
      icon,
      children,
      label,
      disabled,
    };
  };

  const handleOpenChange = newOpen => {
    setIsOpenSwitchWorkspace(newOpen);
  };

  useEffect(() => {
    if (authInfo && authInfo.id && authInfo.workspaceId) {
      dispatch(
        doGetUserSettingEvent({
          userId: authInfo.id,
          workspaceId: authInfo.workspaceId,
        })
      );
      dispatch(
        getWorkspaceUserInfo({
          workspaceId: authInfo.workspaceId,
          userId: authInfo.id,
        })
      );
    }
  }, []);

  useEffect(() => {
    if (
      currentMenu === "customer" &&
      window.location.pathname.split("/")[3] !== "all"
    ) {
      setCurrentMenu("");
    }
  }, [currentMenu]);

  const items = [
    getItem({
      label: t("label.inbox"),
      key: "inbox",
      icon: (
        <span className="anticon anticon-pie-chart ant-menu-item-icon">
          <InboxIcon isActive={currentMenu === "inbox"} />
        </span>
      ),
      disabled: !authInfo.workspaceId,
    }),
    getItem({
      label: t("label.broadcast"),
      key: "send-message",
      icon: (
        <span className="anticon anticon-pie-chart ant-menu-item-icon">
          <SendMessageIcon isActive={currentMenu === "send-message"} />
        </span>
      ),
      disabled: !authInfo.workspaceId,
    }),
    getItem({
      label: t("label.customers"),
      key: "sub1",
      icon: (
        <span className="anticon anticon-pie-chart ant-menu-item-icon">
          <OperatorUserIcon
            isActive={
              currentMenu === "broadcast-list" ||
              (currentMenu === "customer" &&
                window.location.pathname.split("/")[3] === "all")
            }
          />
        </span>
      ),
      children: [
        getItem({
          label: t("label.broadcastLists"),
          key: "broadcast-list",
          disabled: !authInfo.workspaceId,
        }),
        getItem({
          label: t("title.allCustomers"),
          key: "customer",
          disabled: !authInfo.workspaceId,
        }),
      ],
    }),
  ];

  const handleChangeLayout = () => {
    dispatch(changeLayout("ADMIN"));
  };

  useEffect(() => {
    if (authInfo.id && authInfo.workspaceId) {
      dispatch(
        getWorkspaceUserInfo({
          userId: authInfo.id,
          workspaceId: authInfo.workspaceId,
        })
      );
      dispatch(getWorkspaceInfo(authInfo.workspaceId));
    }
    doGetWorkspace();
  }, []);

  useEffect(() => {
    if (authInfo && authInfo.workspaceId) {
      dispatch(doGetWorkspaceEvent({ workspaceId: authInfo.workspaceId }));
    }
  }, [authInfo?.workspaceId]);

  useEffect(() => {
    if (
      workspaceRealtime?.properties?.totalAwaitingReplies !== undefined &&
      workspaceRealtime?.properties?.totalAwaitingReplies > 0
    ) {
      setNumberAwaitingReplies(
        workspaceRealtime?.properties?.totalAwaitingReplies
      );
    } else {
      setNumberAwaitingReplies(workspaceInfo.numberAwaitingReplies);
    }
  }, [
    workspaceRealtime?.properties?.totalAwaitingReplies,
    workspaceInfo?.numberAwaitingReplies,
  ]);

  useEffect(() => {
    dispatch(setCollapsedMenu(collapsed));
  }, [collapsed]);

  return (
    <React.Fragment>
      <Layout.Sider
        width={250}
        collapsedWidth={50}
        breakpoint="lg"
        collapsed={collapsed}
        onBreakpoint={broken => {
          setBroken(broken);
        }}
        onCollapse={collapsed => {
          setCollapsed(collapsed);
        }}
        style={{
          background: COLORS.OPERATOR_MAIN_COLOR,
          position: broken ? "relative" : "fixed",
        }}
        className={styles.antLayout}
      >
        <div>
          <div className={styles.operatorIconLogo}>
            <img
              src={collapsed ? iconLogo : logo}
              alt="logo"
              width={50}
              height={50}
              style={{ width: "100%", maxWidth: 150, height: "auto" }}
            />
          </div>
          {!collapsed && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                padding: "0 8px 12px",
                justifyContent: "center",
              }}
            >
              {authInfo && authInfo.workspaceName && (
                <>
                  {workspaceListItem && workspaceListItem.length > 1 ? (
                    <>
                      <Popover
                        content={
                          <>
                            <SwitchWorkspace
                              setIsOpenSwitchWorkspace={
                                setIsOpenSwitchWorkspace
                              }
                            />
                          </>
                        }
                        trigger="click"
                        open={isOpenSwitchWorkspace}
                        onOpenChange={handleOpenChange}
                        arrow={false}
                        placement="topLeft"
                      >
                        <Button
                          color={"primary"}
                          type="primary"
                          htmlType="button"
                          style={{ overflow: "hidden", maxWidth: 230 }}
                        >
                          <span
                            style={{
                              display: "block",
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                              width: "100%",
                            }}
                            title={
                              workspaceRealtime &&
                              workspaceRealtime.properties &&
                              workspaceRealtime.properties.name !== undefined
                                ? workspaceRealtime.properties.name
                                : authInfo.workspaceName
                            }
                          >
                            {workspaceRealtime &&
                            workspaceRealtime.properties &&
                            workspaceRealtime.properties.name !== undefined
                              ? workspaceRealtime.properties.name
                              : authInfo.workspaceName}
                          </span>
                        </Button>
                      </Popover>
                    </>
                  ) : (
                    <Tag
                      color="#1677ff"
                      className={styles.sidebarWorkspace}
                      title={authInfo.workspaceName}
                    >
                      {authInfo.workspaceName}
                    </Tag>
                  )}
                </>
              )}
            </div>
          )}
          <div style={{ position: "relative", padding: "0 8px" }}>
            <Menu
              mode="inline"
              selectedKeys={[currentMenu]}
              onSelect={handleChangeMenu}
              items={items}
              className={styles.sidebarMenuOperator}
            />
            {!collapsed && numberAwaitingReplies > 0 && (
              <>
                <Badge
                  className={styles.inboxBadge}
                  count={numberAwaitingReplies}
                  color="#F36D44"
                />
              </>
            )}
          </div>
        </div>
        <div className={styles.sidebarLogoBottom}>
          {authInfo.type !== USER_TYPE.SUPER_ADMIN && (
            <Button
              color={"primary"}
              icon={<UsersIcon />}
              onClick={() => {
                handleChangeLayout();
                handleUnlockConversation();
                StorageHelper.removeLocalItem(
                  STORAGE_KEYS.currentConversationId
                );
              }}
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                marginBottom: "10px",
              }}
            >
              {collapsed ? "" : t("button.adminConsole")}
            </Button>
          )}
          {!collapsed && (
            <>
              <Divider style={{ margin: 0 }} />
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "20px 4px",
                }}
              >
                <p style={{ marginBottom: 0 }}>
                  {t("inbox.availableNewMessages")}
                </p>
                <Switch
                  disabled={!authInfo.workspaceId || !authInfo.id}
                  checked={workspaceUserInfo.availableNewMessage}
                  onChange={handleRequestAvailableNewMessage}
                  size="small"
                />
              </div>
              <Divider style={{ margin: 0 }} />
            </>
          )}

          <div
            style={{
              padding: "20px 0",
              textAlign: "center",
            }}
          >
            <Button
              icon={
                collapsed ? (
                  <ChevronRightDoubleIcon />
                ) : (
                  <ChevronLeftDoubleIcon />
                )
              }
              onClick={() => setCollapsed(!collapsed)}
            />
          </div>
        </div>
      </Layout.Sider>
    </React.Fragment>
  );
};

export default OperatorSidebar;
